// Global Instructions Rule Applied!
//
// Two compact, colour-coded age chips for the deal card: days-in-stage and
// days-in-pipeline. Each chip shows the day count and tints to its configured
// threshold band colour (warn/crit); with no band it renders neutral grey.
// Rendered flush-right on the card's company row (see KanbanCard meta slot).

import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock } from '@fortawesome/free-solid-svg-icons'

interface AgeChipProps {
  days: number
  colour: string | null
  tooltip: string
}

const NEUTRAL_BG = '#F3F4F6'
const NEUTRAL_FG = '#6B7280'
const NEUTRAL_BORDER = '#E5E7EB'

function AgeChip({ days, colour, tooltip }: AgeChipProps): React.ReactElement {
  const bg = colour ? `${colour}1A` : NEUTRAL_BG
  const fg = colour || NEUTRAL_FG
  const border = colour ? `${colour}33` : NEUTRAL_BORDER

  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: '14px',
        padding: '1px 5px',
        borderRadius: 6,
        whiteSpace: 'nowrap',
        backgroundColor: bg,
        color: fg,
        border: `1px solid ${border}`
      }}
    >
      <FontAwesomeIcon icon={faClock} style={{ fontSize: 9 }} />
      {days}d
    </span>
  )
}

export interface DealAgeChipsProps {
  daysInStage: number | null
  stageColour: string | null
  daysInPipeline: number | null
  pipelineColour: string | null
}

export default function DealAgeChips({
  daysInStage,
  stageColour,
  daysInPipeline,
  pipelineColour
}: DealAgeChipsProps): React.ReactElement | null {
  if (daysInStage == null && daysInPipeline == null) return null

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      {daysInStage != null && (
        <AgeChip days={daysInStage} colour={stageColour} tooltip={`${daysInStage} day${daysInStage === 1 ? '' : 's'} in stage`} />
      )}
      {daysInPipeline != null && (
        <AgeChip
          days={daysInPipeline}
          colour={pipelineColour}
          tooltip={`${daysInPipeline} day${daysInPipeline === 1 ? '' : 's'} in pipeline`}
        />
      )}
    </span>
  )
}
